import { Link, useParams } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { BarChart3, LayoutGrid, PackageIcon, Settings, Users } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import type { Layout } from './types';

interface NavLink {
  title: string;
  to: string;
  icon: LucideIcon;
  props?: {
    search?: Record<string, string>;
  };
}

const PROJECT_LINKS: NavLink[] = [
  {
    title: 'Requests',
    to: '/projects/$projectId/requests',
    icon: PackageIcon,
    props: {
      search: { status: 'pending' },
    },
  },
  {
    title: 'Statistics',
    to: '/projects/$projectId/stats',
    icon: BarChart3,
  },
  {
    title: 'Users',
    to: '/projects/$projectId/users',
    icon: Users,
  },
  {
    title: 'Settings',
    to: '/projects/$projectId/settings',
    icon: Settings,
  },
];

const GLOBAL_LINKS: NavLink[] = [
  // {
  //   title: 'Dashboard',
  //   to: '/dashboard',
  //   icon: Gauge,
  // },
  {
    title: 'Projects',
    to: '/projects',
    icon: LayoutGrid,
  },
  {
    title: 'Members',
    to: '/members',
    icon: Users,
  },
];

type Props = { layout: Layout; isFree?: boolean } & React.ComponentPropsWithoutRef<
  typeof SidebarGroup
>;

export function NavMain({ layout, isFree, ...props }: Props) {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { isMobile, setOpenMobile } = useSidebar();
  const globalLinks = isFree
    ? GLOBAL_LINKS.filter((link) => link.title !== 'Billing')
    : GLOBAL_LINKS;
  const links = layout === 'global' ? globalLinks : PROJECT_LINKS;

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu>
          {links.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild tooltip={item.title}>
                <Link
                  to={item.to}
                  params={{ projectId }}
                  activeProps={{ className: 'bg-sidebar-accent' }}
                  onClick={handleLinkClick}
                  {...item.props}
                >
                  <item.icon className="text-muted-foreground" />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
